import { useAemConfig } from '@as-react/common-components';
import { useGetSearchContentSuggestionQueryOptions, useGetSearchSuggestionsQueryOptions, } from '@as-react/common-queries';
import { MenuSearchResultBlockVariant, } from '@as-react/common-types';
import { useTranslation } from 'react-i18next';
import { DELAY } from '../constants/GeneralConstants';
import { I18N_NAMESPACES } from '../constants/i18nConstants';
import { SEARCH_RESULT_LIMIT } from '../constants/SearchConstants';
import { hasItems } from '../util/ArrayUtil';
import { getElasticSearchIndexWithEnv, getSearchBrands, getSearchCategories, getSearchContent, getSearchKeywords, getSearchProducts, getSearchResultLimit, hasSearches, wrapSubtitleWithTranslation, } from '../util/SearchUtil';
import { useDebouncedQuery } from './useDebouncedQuery';
const COMBOBOX_ID = 'menu-search';
export const getOptionLabel = (option) => {
    if (!option)
        return '';
    if (option.variant === MenuSearchResultBlockVariant.PRODUCTS) {
        return option.data.title;
    }
    const { name, subtitle } = option.data;
    const subtitleText = subtitle && subtitle.length > 0 ? subtitle.slice(0, 2).join(', ') : '';
    return `${name} ${subtitleText}`.trim();
};
export const useNewMenuSearch = ({ searchTerm = '', suggestionsParentLink, brandsParentLink, categoriesParentLink, }) => {
    var _a, _b, _c, _d, _e;
    const { t } = useTranslation(I18N_NAMESPACES.navigation);
    const aemConfiguration = useAemConfig();
    const { defaultRequestParams, activeEnvironment, elasticsearchIndex } = aemConfiguration;
    const index = getElasticSearchIndexWithEnv(activeEnvironment, elasticsearchIndex);
    const maxAmountEnhancedItems = getSearchResultLimit(searchTerm);
    const selectSuggestions = (data) => ({
        keywords: getSearchKeywords(data === null || data === void 0 ? void 0 : data.keywords, suggestionsParentLink),
        brands: getSearchBrands(data === null || data === void 0 ? void 0 : data.brands, brandsParentLink),
        categories: getSearchCategories(data === null || data === void 0 ? void 0 : data.categories, categoriesParentLink),
        products: getSearchProducts(data === null || data === void 0 ? void 0 : data.products, aemConfiguration.root),
    });
    const selectContentSuggestions = (data) => ({
        content: getSearchContent(data === null || data === void 0 ? void 0 : data.content),
    });
    const { data: suggestions } = useDebouncedQuery(useGetSearchSuggestionsQueryOptions({ query: searchTerm, mainWebshop: defaultRequestParams.mainWebshop, language: defaultRequestParams.language }, undefined, {
        query: {
            enabled: Boolean(searchTerm),
            retry: false,
            placeholderData: prev => prev,
            select: selectSuggestions,
        },
    }), DELAY.DELAY_250);
    const { data: contentSuggestions } = useDebouncedQuery(useGetSearchContentSuggestionQueryOptions({ q: searchTerm, index }, undefined, {
        query: {
            enabled: Boolean(searchTerm),
            retry: false,
            placeholderData: prev => prev,
            select: selectContentSuggestions,
        },
    }), DELAY.DELAY_250);
    const keywords = (_a = suggestions === null || suggestions === void 0 ? void 0 : suggestions.keywords) !== null && _a !== void 0 ? _a : [];
    const categories = (_b = suggestions === null || suggestions === void 0 ? void 0 : suggestions.categories) !== null && _b !== void 0 ? _b : [];
    const brands = (_c = suggestions === null || suggestions === void 0 ? void 0 : suggestions.brands) !== null && _c !== void 0 ? _c : [];
    const products = (_d = suggestions === null || suggestions === void 0 ? void 0 : suggestions.products) !== null && _d !== void 0 ? _d : [];
    const content = (_e = contentSuggestions === null || contentSuggestions === void 0 ? void 0 : contentSuggestions.content) !== null && _e !== void 0 ? _e : [];
    const searchesKeywords = keywords.slice(0, SEARCH_RESULT_LIMIT.FOR_SEARCH_SUGGESTIONS);
    const searchesBrandOrCat = hasItems(keywords) || hasSearches(categories, brands)
        ? [
            ...brands.slice(0, maxAmountEnhancedItems),
            ...categories.slice(0, maxAmountEnhancedItems).map(item => wrapSubtitleWithTranslation(item, true, t)),
        ].slice(0, SEARCH_RESULT_LIMIT.FOR_SEARCH_BRANDS_OR_CATEGORIES)
        : [];
    const searchesProducts = products.slice(0, SEARCH_RESULT_LIMIT.FOR_SEARCH_PRODUCTS);
    const contentItems = content.slice(0, maxAmountEnhancedItems);
    const hasSuggestionResults = hasItems(searchesKeywords) || hasItems(searchesBrandOrCat);
    const hasContentResults = hasItems(contentItems);
    const hasProductResults = hasItems(searchesProducts);
    const hasSearchResults = hasSuggestionResults || hasProductResults || hasContentResults;
    let flatIndexCursor = 0;
    const nextFlatIndex = () => flatIndexCursor++;
    const keywordOptions = searchesKeywords.map((data, i) => ({
        optionId: `${COMBOBOX_ID}-option-searches-keyword-${i}`,
        flatIndex: nextFlatIndex(),
        variant: MenuSearchResultBlockVariant.SUGGESTIONS,
        data,
    }));
    const searchesBrandOrCatOptions = searchesBrandOrCat.map((data, i) => ({
        optionId: `${COMBOBOX_ID}-option-searches-item-${i}`,
        flatIndex: nextFlatIndex(),
        variant: MenuSearchResultBlockVariant.BRAND_OR_CAT,
        data,
    }));
    const productOptions = searchesProducts.map((data, i) => ({
        optionId: `${COMBOBOX_ID}-option-products-item-${i}`,
        flatIndex: nextFlatIndex(),
        variant: MenuSearchResultBlockVariant.PRODUCTS,
        data,
    }));
    const contentOptions = contentItems.map((data, i) => ({
        optionId: `${COMBOBOX_ID}-option-content-item-${i}`,
        flatIndex: nextFlatIndex(),
        variant: MenuSearchResultBlockVariant.CONTENT,
        data,
    }));
    const flatOptions = [
        ...keywordOptions,
        ...searchesBrandOrCatOptions,
        ...productOptions,
        ...contentOptions,
    ];
    return {
        hasSearchResults,
        hasSuggestionResults,
        hasProductResults,
        hasContentResults,
        contentItems,
        keywordOptions,
        searchesBrandOrCatOptions,
        productOptions,
        contentOptions,
        flatOptions,
    };
};
